import { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
  Check, Plus, Search, ChevronDown, ChevronUp, Sparkles,
  ClipboardCheck, Clock, Dumbbell,
} from "lucide-react";
import { useI18n } from "../lib/i18n";
import { getExerciseName } from "../lib/exerciseI18n";
import { API } from "../App";

const ExerciseSelector = ({ patientId, selectedExercises, onSelectionChange, suggestedExercises }) => {
  const { t } = useI18n();

  const CATEGORY_LABELS = {
    cognitive: t("library.cognitive", "Cognitif"),
    behavioral: t("library.behavioral", "Comportemental"),
    monitoring: t("library.monitoring", "Suivi"),
    mindfulness: t("library.mindfulness", "Pleine conscience"),
    positive: t("library.positive", "Positif"),
    "self-awareness": t("library.selfAwareness", "Connaissance de soi"),
    organization: t("library.organization", "Organisation"),
    act: "ACT",
    barkley: t("library.barkleyParents", "Barkley — Parents"),
    barkley_child: t("library.barkleyChildren", "Barkley — Enfants"),
    barkley_teen: t("library.barkleyTeens", "Barkley — Ados"),
    tdah_adulte: t("library.tdahAdulte", "TDAH Adulte"),
  };
  const [exerciseTypes, setExerciseTypes] = useState([]);
  const [history, setHistory] = useState({ assignments: [], standalone_completed: [] });
  const [catalogOpen, setCatalogOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    axios.get(`${API}/exercises/types`, { withCredentials: true })
      .then(res => setExerciseTypes(res.data))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!patientId) return;
    axios.get(`${API}/session-reports/patient/${patientId}/exercise-history`, { withCredentials: true })
      .then(res => setHistory(res.data))
      .catch(() => {});
  }, [patientId]);

  const exerciseMap = useMemo(() => {
    const map = {};
    exerciseTypes.forEach(e => { map[e.type_id] = e; });
    return map;
  }, [exerciseTypes]);

  const completedTypeIds = useMemo(() => {
    const ids = new Set();
    history.assignments.filter(a => a.status === "completed").forEach(a => ids.add(a.exercise_type));
    history.standalone_completed.forEach(s => ids.add(s.exercise_type));
    return ids;
  }, [history]);

  const pendingAssignments = useMemo(() =>
    history.assignments.filter(a => a.status === "pending"),
  [history]);

  const toggleExercise = (typeId) => {
    const current = selectedExercises || [];
    if (current.includes(typeId)) {
      onSelectionChange(current.filter(id => id !== typeId));
    } else {
      onSelectionChange([...current, typeId]);
    }
  };

  const grouped = useMemo(() => {
    const groups = {};
    const filtered = exerciseTypes.filter(e =>
      !searchTerm || e.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      e.description.toLowerCase().includes(searchTerm.toLowerCase())
    );
    filtered.forEach(e => {
      const cat = e.category || "other";
      if (!groups[cat]) groups[cat] = [];
      groups[cat].push(e);
    });
    return groups;
  }, [exerciseTypes, searchTerm]);

  const isSelected = (typeId) => (selectedExercises || []).includes(typeId);

  if (!patientId) {
    return (
      <Card className="border-dashed opacity-60">
        <CardContent className="py-6 text-center text-sm text-muted-foreground">
          <Dumbbell className="w-6 h-6 mx-auto mb-2" />
          {t("sessions.selectPatientFirst") || "Sélectionnez un patient pour gérer les exercices"}
        </CardContent>
      </Card>
    );
  }

  return (
    <Card data-testid="exercise-selector">
      <CardHeader className="pb-3">
        <CardTitle className="font-heading text-lg flex items-center gap-2">
          <Dumbbell className="w-5 h-5" />
          {t("sessions.field.exercices") || "Exercices"}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">

        {/* AI Suggested Exercises */}
        {suggestedExercises && suggestedExercises.length > 0 && (
          <div className="space-y-2" data-testid="ai-suggestions">
            <p className="text-sm font-medium flex items-center gap-1.5 text-amber-700 dark:text-amber-400">
              <Sparkles className="w-4 h-4" />
              {t("sessions.aiSuggestions") || "Suggestions IA"}
            </p>
            <div className="grid gap-2">
              {suggestedExercises.map(s => {
                const ex = exerciseMap[s.type_id];
                if (!ex) return null;
                const selected = isSelected(s.type_id);
                return (
                  <div
                    key={s.type_id}
                    onClick={() => toggleExercise(s.type_id)}
                    className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                      selected
                        ? "bg-amber-50 border-amber-300 dark:bg-amber-950 dark:border-amber-700"
                        : "hover:bg-muted/50 border-border"
                    }`}
                    data-testid={`suggestion-${s.type_id}`}
                  >
                    <div className={`w-5 h-5 rounded border-2 flex items-center justify-center shrink-0 mt-0.5 ${
                      selected ? "bg-amber-500 border-amber-500 text-white" : "border-muted-foreground/30"
                    }`}>
                      {selected && <Check className="w-3 h-3" />}
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-medium">{getExerciseName(t, ex.type_id, ex.name)}</p>
                      <p className="text-xs text-muted-foreground">{s.reason}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Patient History: Pending */}
        {pendingAssignments.length > 0 && (
          <div className="space-y-2" data-testid="pending-exercises">
            <p className="text-sm font-medium flex items-center gap-1.5 text-blue-600 dark:text-blue-400">
              <Clock className="w-4 h-4" />
              {t("sessions.pendingExercises") || "Exercices en cours"}
              <span className="text-xs bg-blue-100 dark:bg-blue-950 text-blue-700 dark:text-blue-300 px-1.5 py-0.5 rounded-full">
                {pendingAssignments.length}
              </span>
            </p>
            <div className="flex flex-wrap gap-2">
              {pendingAssignments.map(a => (
                <span key={a.assignment_id} className="text-xs px-2.5 py-1 rounded-full bg-blue-50 text-blue-700 dark:bg-blue-950 dark:text-blue-300 border border-blue-200 dark:border-blue-800">
                  {a.title}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Patient History: Completed */}
        {completedTypeIds.size > 0 && (
          <div className="space-y-2" data-testid="completed-exercises">
            <p className="text-sm font-medium flex items-center gap-1.5 text-emerald-600 dark:text-emerald-400">
              <ClipboardCheck className="w-4 h-4" />
              {t("sessions.completedExercises") || "Exercices complétés"}
              <span className="text-xs bg-emerald-100 dark:bg-emerald-950 text-emerald-700 dark:text-emerald-300 px-1.5 py-0.5 rounded-full">
                {completedTypeIds.size}
              </span>
            </p>
            <div className="flex flex-wrap gap-2">
              {[...completedTypeIds].map(typeId => {
                const ex = exerciseMap[typeId];
                return (
                  <span key={typeId} className="text-xs px-2.5 py-1 rounded-full bg-emerald-50 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-800">
                    {ex?.name || typeId}
                  </span>
                );
              })}
            </div>
          </div>
        )}

        {/* Selected exercises to assign */}
        {(selectedExercises || []).length > 0 && (
          <div className="space-y-2" data-testid="selected-exercises">
            <p className="text-sm font-medium flex items-center gap-1.5">
              <Check className="w-4 h-4 text-primary" />
              {t("sessions.exercisesToAssign") || "Exercices à assigner"}
              <span className="text-xs bg-primary/10 text-primary px-1.5 py-0.5 rounded-full">
                {selectedExercises.length}
              </span>
            </p>
            <div className="flex flex-wrap gap-2">
              {selectedExercises.map(typeId => {
                const ex = exerciseMap[typeId];
                return (
                  <span
                    key={typeId}
                    onClick={() => toggleExercise(typeId)}
                    className="text-xs px-2.5 py-1 rounded-full bg-primary/10 text-primary border border-primary/30 cursor-pointer hover:bg-primary/20 transition-colors flex items-center gap-1"
                  >
                    {ex?.name || typeId}
                    <span className="text-primary/50">&times;</span>
                  </span>
                );
              })}
            </div>
          </div>
        )}

        {/* Exercise Catalog */}
        <div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setCatalogOpen(!catalogOpen)}
            className="w-full justify-between"
            data-testid="toggle-catalog"
          >
            <span className="flex items-center gap-1.5">
              <Plus className="w-4 h-4" />
              {t("sessions.browseCatalog") || "Parcourir le catalogue"}
            </span>
            {catalogOpen ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </Button>

          {catalogOpen && (
            <div className="mt-3 space-y-3" data-testid="exercise-catalog">
              <div className="relative">
                <Search className="absolute left-3 top-2.5 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder={t("sessions.searchExercises") || "Rechercher un exercice..."}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-9"
                  data-testid="search-exercises"
                />
              </div>

              <div className="max-h-80 overflow-y-auto space-y-3 pr-1">
                {Object.entries(grouped).map(([cat, exercises]) => (
                  <div key={cat}>
                    <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1.5">
                      {CATEGORY_LABELS[cat] || cat}
                    </p>
                    <div className="space-y-1">
                      {exercises.map(ex => {
                        const selected = isSelected(ex.type_id);
                        const completed = completedTypeIds.has(ex.type_id);
                        return (
                          <div
                            key={ex.type_id}
                            onClick={() => toggleExercise(ex.type_id)}
                            className={`flex items-center gap-2.5 p-2 rounded-md cursor-pointer transition-colors text-sm ${
                              selected
                                ? "bg-primary/10 text-primary"
                                : "hover:bg-muted/50"
                            }`}
                            data-testid={`catalog-${ex.type_id}`}
                          >
                            <div className={`w-4 h-4 rounded border flex items-center justify-center shrink-0 ${
                              selected ? "bg-primary border-primary text-white" : "border-muted-foreground/30"
                            }`}>
                              {selected && <Check className="w-3 h-3" />}
                            </div>
                            <span className="truncate">{getExerciseName(t, ex.type_id, ex.name)}</span>
                            {completed && (
                              <ClipboardCheck className="w-3.5 h-3.5 text-emerald-500 shrink-0 ml-auto" />
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default ExerciseSelector;
